// Step 19 — AI intelligence workspace.
//
// This is the AI-native "deal operating view". It answers:
//   - What is happening?              → AIExecutiveSituation
//   - What is unusual / matters?      → AIAttentionQueue
//   - Why does it matter (evidence)?  → AIEvidencePanel (inside each card)
//   - What should a human review?     → same queue, flagged
//   - Buyer / contract intelligence   → dedicated sections
//   - Recent activity                 → deal-situation preparedness notes
//
// Composition of three EXISTING endpoints — no new backend:
//   - `/ai/context/` (via AIWorkspaceContext) for company + deal snapshots
//   - `getDealSituationLive(workspaceId)` — synthesized buyer/portfolio/
//     preparedness/suggestions (already deterministic, no LLM)
//   - `listContracts(workspaceId)` — contract drafts (Step 16 output)
//   - `listDealActions(workspaceId)` — deal actions (Step 13 output)
//
// The intelligence workspace never writes. Human approval flows for
// contracts and actions remain in their existing dedicated pages;
// findings here link INTO those flows, not around them.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getDealSituationLive,
  listContracts,
  listDealActions,
} from "../api";
import {
  AIEmpty,
  AIError,
  AILoading,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";
import AIAttentionQueue from "../shell/intelligence/AIAttentionQueue";
import AIBuyerInsight from "../shell/intelligence/AIBuyerInsight";
import AIExecutiveSituation from "../shell/intelligence/AIExecutiveSituation";
import AIFindingCard from "../shell/intelligence/AIFindingCard";
import {
  KIND,
  buildAttentionQueue,
  extractBuyerFindings,
  extractContractFindings,
  extractPreparednessFindings,
  extractRiskFindings,
} from "../shell/intelligence/helpers";

const HIGH_PRIORITY_LIMIT = 5;
const QUEUE_LIMIT = 12;

function CrossBuyerPatterns({ patterns }) {
  const p = patterns || {};
  const groups = [
    { key: "information_requested", label: "Informationsförfrågningar" },
    { key: "objection", label: "Invändningar" },
    { key: "requirement", label: "Krav" },
    { key: "question", label: "Frågor" },
  ];
  const hasAny = groups.some((g) => (p[g.key] || []).length > 0);
  if (!hasAny) {
    return (
      <AIEmpty
        title="Inga återkommande mönster registrerade"
        hint="Mönster uppstår när minst två köpare rest samma sak."
      />
    );
  }
  return (
    <div className="qai-cross-patterns">
      {groups.map((g) => {
        const items = p[g.key] || [];
        if (items.length === 0) return null;
        return (
          <div key={g.key} className="qai-cross-pattern-group">
            <h4 className="qai-cross-pattern-title">{g.label}</h4>
            <ul className="qai-cross-pattern-list">
              {items.slice(0, 5).map((it, i) => (
                <li key={i} className="qai-cross-pattern-item">
                  <span className="qai-cross-pattern-quote">
                    "{it.normalized_text}"
                  </span>
                  <span className="qai-cross-pattern-count">
                    {it.acquirer_count} köpare
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export default function AIIntelligence() {
  const {
    loading: shellLoading,
    activeWorkspace,
    activeWorkspaceId,
    companyIntel,
    dealIntel,
  } = useAIWorkspace();

  const [situation, setSituation] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!activeWorkspaceId) return;
      setLoading(true);
      setError(null);
      try {
        const [sit, cont, act] = await Promise.all([
          getDealSituationLive(activeWorkspaceId, { signal }),
          listContracts(activeWorkspaceId, { limit: 200 }, { signal }),
          listDealActions(activeWorkspaceId, { limit: 200 }, { signal }),
        ]);
        setSituation(sit);
        setContracts(cont?.results || []);
        setActions(act?.results || []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda intelligence.");
      } finally {
        setLoading(false);
      }
    },
    [activeWorkspaceId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  const preparedness = situation?.preparedness_observations || [];
  const suggestions = situation?.suggested_actions || [];
  const portfolio = situation?.buyer_portfolio || [];
  const patterns = situation?.cross_buyer_patterns || {};
  const commSummary = situation?.communication_summary || null;

  const queue = useMemo(
    () =>
      buildAttentionQueue({
        dealSnapshot: dealIntel,
        preparedness,
        portfolio,
        contracts,
        actions,
        suggestions,
      }),
    [dealIntel, preparedness, portfolio, contracts, actions, suggestions],
  );

  const riskFindings = useMemo(
    () => extractRiskFindings(dealIntel),
    [dealIntel],
  );
  const preparednessFindings = useMemo(
    () => extractPreparednessFindings(preparedness),
    [preparedness],
  );
  const buyerFindings = useMemo(
    () => extractBuyerFindings(portfolio),
    [portfolio],
  );
  const contractFindings = useMemo(
    () => extractContractFindings(contracts),
    [contracts],
  );

  if (shellLoading) return <AILoading />;

  if (!activeWorkspaceId) {
    return (
      <>
        <AIPageHeader title="Intelligence" />
        <AIEmpty title="Ingen affär vald" />
      </>
    );
  }

  return (
    <>
      <AIPageHeader
        title="Intelligence"
        subtitle={activeWorkspace?.organisation_name}
      />

      <AIError message={error} />
      {loading && !situation && (
        <div className="qai-page-section-hint" style={{ marginBottom: 8 }}>
          Laddar aggregerad intelligence…
        </div>
      )}

      {/* Executive situation — pure facts */}
      <AIExecutiveSituation
        dealSnapshot={dealIntel}
        companySnapshot={companyIntel}
        communicationSummary={commSummary}
      />

      {/* Attention queue — deterministic prioritisation over the
          extractors' output. Every entry is a real finding backed by
          existing rows; no fabricated scores. */}
      <AISection
        title="Kräver uppmärksamhet"
        right={
          queue.length > QUEUE_LIMIT ? (
            <span className="qai-page-section-hint">
              Visar {QUEUE_LIMIT} av {queue.length}
            </span>
          ) : null
        }
      >
        <AIAttentionQueue
          items={queue}
          workspaceId={activeWorkspaceId}
          limit={QUEUE_LIMIT}
        />
      </AISection>

      {/* Risks — explicit page section for the risk profile facts,
          separate from the attention queue so users can browse them
          in isolation. */}
      <AISection
        title="Risker & anomalier"
        right={
          <Link to={`/deals/${activeWorkspaceId}/risks`} className="qai-page-section-link">
            Öppna risker →
          </Link>
        }
      >
        {riskFindings.length === 0 ? (
          <AIEmpty
            title="Ingen risk registrerad"
            hint="Bedöm riskprofilen i broker-portalen för att fylla på detta underlag."
          />
        ) : (
          <div className="qai-attention-queue">
            {riskFindings.slice(0, HIGH_PRIORITY_LIMIT).map((f) => (
              <AIFindingCard key={f.key} finding={f} workspaceId={activeWorkspaceId} />
            ))}
          </div>
        )}
      </AISection>

      {/* Buyer / acquirer intelligence — from the deal-situation aggregate. */}
      <AISection
        title="Köparbild"
        right={
          <Link to={`/deals/${activeWorkspaceId}/buyers`} className="qai-page-section-link">
            Alla köpare →
          </Link>
        }
      >
        {portfolio.length === 0 ? (
          <AIEmpty
            title="Inga köpare registrerade ännu"
            hint="Köparlistan fylls i via broker-portalens outreach-flöde."
          />
        ) : (
          <>
            <div className="qai-buyer-insight-grid">
              {portfolio.slice(0, 6).map((row) => (
                <AIBuyerInsight
                  key={row.acquirer.id}
                  row={row}
                  workspaceId={activeWorkspaceId}
                />
              ))}
            </div>
            {buyerFindings.length > 0 && (
              <div className="qai-attention-queue" style={{ marginTop: 12 }}>
                {buyerFindings.slice(0, HIGH_PRIORITY_LIMIT).map((f) => (
                  <AIFindingCard key={f.key} finding={f} workspaceId={activeWorkspaceId} />
                ))}
              </div>
            )}
          </>
        )}
      </AISection>

      {/* Cross-buyer patterns — recurring info requests / objections
          across buyers, already deterministic on the backend. */}
      <AISection title="Återkommande mönster">
        <CrossBuyerPatterns patterns={patterns} />
      </AISection>

      {/* Contract findings — uncertainties + pending-review drafts.
          Approval and e-signature flows live in the existing
          ContractsPanel; this section only surfaces the findings. */}
      <AISection
        title="Kontraktsfynd"
        right={
          <Link
            to={`/deals/${activeWorkspaceId}/contracts`}
            className="qai-page-section-link"
          >
            Alla avtal →
          </Link>
        }
      >
        {contractFindings.length === 0 ? (
          <AIEmpty
            title="Inga registrerade fynd i kontraktsutkasten"
            hint="Fynd dyker upp när ett utkast har osäkerheter eller väntar granskning."
          />
        ) : (
          <div className="qai-attention-queue">
            {contractFindings.slice(0, HIGH_PRIORITY_LIMIT).map((f) => (
              <AIFindingCard key={f.key} finding={f} workspaceId={activeWorkspaceId} />
            ))}
          </div>
        )}
      </AISection>

      {/* Recommended review areas — the suggestions bucket carries
          user_action_required=true from the backend, and the finding
          card renders that flag explicitly. */}
      {preparednessFindings.length > 0 && (
        <AISection title="Rekommenderade områden att granska">
          <div className="qai-attention-queue">
            {preparednessFindings.map((f) => (
              <AIFindingCard key={f.key} finding={f} workspaceId={activeWorkspaceId} />
            ))}
          </div>
        </AISection>
      )}

      {/* Explicit human-vs-AI disclaimer — same wording the backend
          returns on the aggregate endpoint. */}
      {situation?.assistive_only && (
        <div className="qai-shell-empty-hint" style={{ marginTop: 20 }}>
          {situation.assistive_only}
        </div>
      )}
    </>
  );
}
